import React, { createContext, useMemo } from "react";
import { createTheme, ThemeProvider as MuiThemeProvider } from "@mui/material/styles";
import { trTR } from "@mui/material/locale";
import useAuth from "../hooks/useAuth";
import { generatePalette, getContrastText } from "./themeUtils";
import { DEFAULT_PRIMARY } from "./palettes";

export const ClinicThemeContext = createContext(null);

function resolveClinicColor(auth) {
  return (
    auth?.user?.clinic?.themeColor ||
    auth?.clinic?.themeColor ||
    null
  );
}

function buildMuiTheme(palette) {
  return createTheme(
    {
      palette: {
        primary: {
          main: palette.primary,
          dark: palette.primaryDark,
          light: palette.primaryLight,
          contrastText: palette.contrastText,
        },
        secondary: {
          main: palette.secondary,
        },
        success: { main: palette.success },
        error: { main: palette.error },
        warning: { main: palette.warning },
        info: { main: palette.primary },
      },
      shape: { borderRadius: 8 },
      typography: {
        fontFamily: "'Inter', 'Segoe UI', sans-serif",
        button: { textTransform: "none", fontWeight: 700 },
      },
      components: {
        MuiButton: {
          styleOverrides: {
            containedPrimary: {
              boxShadow: `0 4px 14px ${palette.shadow}`,
              "&:hover": {
                boxShadow: `0 6px 20px ${palette.shadowStrong}`,
              },
            },
          },
        },
        MuiChip: {
          styleOverrides: {
            colorPrimary: {
              backgroundColor: palette.primary,
              color: getContrastText(palette.primary),
            },
          },
        },
      },
    },
    trTR
  );
}

export default function ClinicThemeProvider({ children }) {
  const { auth } = useAuth();

  const clinicColor = resolveClinicColor(auth);
  const primaryHex = clinicColor || DEFAULT_PRIMARY;

  const palette = useMemo(() => generatePalette(primaryHex), [primaryHex]);
  const muiTheme = useMemo(() => buildMuiTheme(palette), [palette]);

  const ctx = useMemo(
    () => ({
      palette,
      clinicColor,
      isCustomTheme: !!clinicColor,
    }),
    [palette, clinicColor]
  );

  return (
    <ClinicThemeContext.Provider value={ctx}>
      <MuiThemeProvider theme={muiTheme}>{children}</MuiThemeProvider>
    </ClinicThemeContext.Provider>
  );
}
